import { Component, Event, EventEmitter, h, Prop, State, Watch } from '@stencil/core';
import { i18nConfig, EmailCollection } from '../../../../../interfaces';
import { timePassed } from '../../../../../util';

@Component({
  tag: 'presto-email-overlay-controller',
  styleUrl: 'presto-email-overlay-controller.css',
  shadow: false,
})
export class PrestoEmailOverlayController {
  @Prop() ended: boolean;
  @Prop({ reflect: true }) currentTime: number;
  @Prop({ reflect: true }) duration: number;
  @Prop() direction?: 'rtl';
  @Prop() emailCollection?: EmailCollection;
  @Prop() i18n: i18nConfig;
  @Prop() videoId: number;
  @Prop() presetId: number;
  @Prop() provider: string;

  @State() enabled: boolean;
  @State() show: boolean;
  @State() loading: boolean;
  @State() error: string;
  @State() percentagePassed: number = 0;

  @Event() playVideo: EventEmitter<void>;
  @Event() pauseVideo: EventEmitter<true>;
  @Event() restartVideo: EventEmitter<void>;
  @Event() emailStateChange: EventEmitter<boolean>;

  /**
   * Set email collection in local storage
   * @param status string
   */
  setStorage(status) {
    window.localStorage.setItem('presto.videos.email_collection', JSON.stringify({ [this.videoId]: status }));
  }

  /**
   * Get email collection in local storage
   * @returns status string
   */
  getStorage() {
    return window.localStorage.getItem('presto.videos.email_collection');
  }

  componentWillLoad() {
    this.handleDuration();
  }

  /**
   * Wait for duration to start before checking time
   * @returns void
   */
  @Watch('duration')
  handleDuration() {
    this.enabled = this.getStorage() ? false : this?.emailCollection?.enabled;
    this.handleTimeCheck();
  }

  /**
   * When current time changes, check to see if we should
   * enable the overlay
   * @returns void
   */
  @Watch('currentTime')
  handleTimeCheck() {
    if (!this.enabled) return; // not enabled
    if (this.getStorage()) return; // already skipped
    this.checkTime();
  }

  @Watch('currentTime')
  handleShowChange() {
    if (!this.show) return; // bail if not showing.
    this.pauseVideo.emit(true); // pause video if here.
  }

  /**
   * Set enabled/disabled based on time that has passed
   */
  checkTime() {
    this.show = timePassed({
      current: this.currentTime,
      duration: this.duration,
      showAfter: this?.emailCollection?.percentage || 0,
    });
  }

  /**
   * Post the submission to the email collection handler
   * @param e Event
   * @param nonce string
   * @returns Response
   */
  async post(e, nonce) {
    return await fetch(window?.prestoPlayer?.ajaxurl, {
      method: 'post',
      body: new URLSearchParams({
        action: 'presto_player_email_submit',
        nonce,
        preset_id: this.presetId,
        video_id: this.videoId,
        provider: this.provider,
        ...(e?.detail || {}),
      }),
    });
  }

  /**
   * Get a fresh nonce.
   *
   * The nonce we send is baked into the page markup, so a full page cache that
   * outlives it hands every visitor a dead one. This endpoint is open to logged
   * out visitors and always registered, even when analytics is off.
   * @returns string
   */
  async refreshNonce() {
    try {
      const response = await fetch(`${window?.prestoPlayer?.ajaxurl}?action=presto_refresh_progress_nonce`);
      const { success, data } = await response.json();
      return success && typeof data === 'string' ? data : '';
    } catch (e) {
      return '';
    }
  }

  /**
   * Pull a message out of an error payload.
   *
   * Validation failures come back as an array of messages, everything else as a
   * plain string, so this can't just index into it.
   * @param data any
   * @returns string
   */
  getErrorMessage(data) {
    const message = Array.isArray(data) ? data[0] : data;
    return message && typeof message === 'string' ? message : '';
  }

  /**
   * Submit email collection
   * @param e Event
   */
  async submit(e) {
    this.loading = true;
    this.error = '';

    // handle submit
    try {
      let response = await this.post(e, window?.prestoPlayer?.nonce);

      // an expired nonce is the only thing that 403s here. get a fresh one and
      // give it exactly one more try.
      if (response?.status === 403) {
        const nonce = await this.refreshNonce();
        if (nonce) {
          response = await this.post(e, nonce);
        }
      }

      const { success, data } = await response.json();
      if (success) {
        this.setStorage('collected');
        this.show = false;
        this.playVideo.emit();
      } else {
        // the server's nonce wording is internal, so fall through to ours.
        const message = response?.status === 403 ? '' : this.getErrorMessage(data);
        this.error = message || this?.i18n?.emailError;
      }
    } catch {
      // only fetch/json rejections land here, and those carry nothing worth showing.
      this.error = this?.i18n?.emailError;
    } finally {
      this.loading = false;
    }
  }

  /**
   * Skip email collection
   */
  skip() {
    this.setStorage('skipped');
    this.show = false;
    this.playVideo.emit();
  }

  /**
   * Maybe render
   * @returns JSX
   */

  @Watch('show')
  handleEmailStateChange(val) {
    this.emailStateChange.emit(val);
  }

  render() {
    if (!this.show) {
      return;
    }

    return (
      <presto-email-overlay-ui
        style={{
          ...(this?.emailCollection?.button_color
            ? {
                '--presto-player-button-color': `${this?.emailCollection?.button_color}`,
              }
            : {}),
          ...(this?.emailCollection?.button_text_color
            ? {
                '--presto-player-button-text': `${this?.emailCollection?.button_text_color}`,
              }
            : {}),
        }}
        direction={this.direction}
        class="email-overlay"
        headline={this?.emailCollection?.headline}
        bottomText={this?.emailCollection?.bottom_text}
        allowSkip={this?.emailCollection?.allow_skip}
        buttonText={this?.emailCollection?.button_text}
        isLoading={this.loading}
        errorMessage={this.error}
        onSubmitForm={e => this.submit(e)}
        onSkip={() => this.skip()}
        i18n={this.i18n}
        provider={this.provider}
      ></presto-email-overlay-ui>
    );
  }
}
